import { useState, useContext } from "react";
import { useApolloClient } from "@apollo/client";
import settingsQuery from "../graphql/settings.query.graphql";
import {
  ContextEDH,
  defaultBookingClassroomOrder,
  defaultCampusCategoriesConfig,
  defaultGroupInLinkRestriction,
  defaultInstitutionThemePreferences,
  defaultRestrictionConfigurations,
} from "@context/ContextEDH";
import {
  BookingClassroomOrderPayload,
  CampusCategoriesConfig,
  GroupsManagerType,
  Types,
} from "@context/base.reducer";
import { BaseQuery, SettingFilterInput } from "@models/ISchema";
import { Setting } from "@models/ISchema";
import { applyLocaleOverrides } from "@config/i18n";
import {
  setInstructorLinkTags,
  setAreSettingsLoaded,
  setIsAgentsChatEnabled,
  setIsApprovalFlowEnabled,
  setIsProgrammingStructuresEnabled,
  setRuleCourseTypeSetting,
  setShowContractedBlocksInSchedule,
  setShowKeepSessionDurationCheckbox,
  setShowRetakersColumns,
  setValidateInstructorsAfterRemoval,
  setCanPublishPackages,
} from "@context/useSettingsStore";
import { DEFAULT_RULES_COURSE_TYPES } from "@services/constants/settings";
import {
  InstructorLinkTagsSetting,
  InstructorRemovalValidationSetting,
} from "@services/types/settings/settingsTypes";

enum settingsType {
  institutionCode = "Institution.code",
  mouseflowProjectId = "Mouseflow.projectId",
  groupsManagerHideColumnsFilter = "GroupsManager.hide_columns_filter",
  groupsManagerConfiguration = "GroupsManager.configuration",
  SchedulerEditorCreateGroupWithinLinkCreation = "SchedulerEditor.create_group_within_link_creation",
  SchedulerEditorRestrictGroupsInLink = "SchedulerEditor.restrict_groups_in_link",
  SchedulerEditorOrderClassroomBookings = "SchedulerEditor.order_classroom_bookings",
  SchedulerEditorCampusCategoriesConfig = "SchedulerEditor.campus_categories_config",
  SchedulerEditorClearLinkResources = "SchedulerEditor.clear_link_resources",
  BookingsProgramRequired = "Bookings.program_required",
  SchedulerEditorAllowLinkCreationWithoutNewSections = "SchedulerEditor.allow_link_creation_without_new_sections",
  RestrictionConfigurations = "Restriction.configurations",
  InstitutionThemePreferences = "Institution.theme_preferences",
  CustomI18nOverrides = "Institution.custom_i18n_overrides",
  SchedulerEditorMaxInstructorsAssignmentByLink = "SchedulerEditor.max_instructors_assignment_by_link",
  SchedulerEditorShowRetakersColumns = "SchedulerEditor.show_retakers_columns",
  SchedulerEditorInstructorLinkTags = "SchedulerEditor.instructor_link_tags",
  SchedulerEditorShowContractedBlocksInSchedule = "SchedulerEditor.show_contracted_blocks_in_schedule",
  SchedulerEditorShowKeepSessionDurationCheckbox = "SchedulerEditor.show_keep_session_duration_checkbox",
  SchedulerEditorValidateInstructorsAfterRemoval = "SchedulerEditor.validate_instructors_after_removal",
  CoreProgrammingStructures = "Core.programming_structures",
  AgentsChat = "Agents.chat",
  PackageConfigurations = "Package.configurations",
  VersionControl = "VersionControl.configurations",
}

type SettingsObj = { [key: string]: Setting };

const TRUTHY_SETTING_VALUES = ["1", 1, "true", true] as const;

const getSettingsObj = (settings: Setting[]) => {
  const settingsObj: SettingsObj = {};

  settings.forEach(setting => {
    settingsObj[setting.config] = setting;
  });

  return settingsObj;
};

const getGroupsInLinkRestriction = (settingsObj: SettingsObj) => {
  let groupsInLinkRestrictionValue = defaultGroupInLinkRestriction;
  const groupsInLinkRestriction = settingsObj[settingsType?.SchedulerEditorRestrictGroupsInLink];

  if (groupsInLinkRestriction?.value) {
    groupsInLinkRestrictionValue = JSON.parse(groupsInLinkRestriction?.value) as {
      enabled: boolean;
      groups_allowed: number;
    };
  }

  return groupsInLinkRestrictionValue;
};

const getCampusCategoriesConfig = (settingsObj: SettingsObj) => {
  let campusCategoriesConfigValue = defaultCampusCategoriesConfig;
  const campusCategoriesConfig = settingsObj[settingsType?.SchedulerEditorCampusCategoriesConfig];

  if (campusCategoriesConfig?.value) {
    campusCategoriesConfigValue = JSON.parse(
      campusCategoriesConfig?.value,
    ) as CampusCategoriesConfig;
  }

  return campusCategoriesConfigValue;
};

const getAllowLinkCreationWithoutNewSections = (settingsObj: SettingsObj) => {
  const allowLinkCreationWithoutNewSections =
    settingsObj[settingsType?.SchedulerEditorAllowLinkCreationWithoutNewSections];

  const value = !!allowLinkCreationWithoutNewSections?.value
    ? JSON.parse(allowLinkCreationWithoutNewSections?.value)
    : false;
  const isActive = TRUTHY_SETTING_VALUES.includes(value);

  return isActive;
};

const getRestrictionConfigurations = (settingsObj: SettingsObj) => {
  const restrictionConfigurations = settingsObj[settingsType?.RestrictionConfigurations];

  const value = !!restrictionConfigurations?.value
    ? JSON.parse(restrictionConfigurations?.value)
    : defaultRestrictionConfigurations;

  return {
    ...value,
    loaded: true,
  };
};

const getInstitutionThemePreferences = (settingsObj: SettingsObj) => {
  const institutionThemePreferences = settingsObj[settingsType?.InstitutionThemePreferences];
  const value = !!institutionThemePreferences?.value
    ? JSON.parse(institutionThemePreferences?.value)
    : structuredClone(defaultInstitutionThemePreferences);

  return value;
};

const getGroupsManagerConfiguration = (settingsObj: SettingsObj) => {
  const groupsManagerConfiguration = settingsObj[settingsType?.groupsManagerConfiguration];
  const value = !!groupsManagerConfiguration?.value
    ? JSON.parse(groupsManagerConfiguration?.value)
    : null;

  return value;
};

const getMaxInstructorsAssignmentByLink = (settingsObj: SettingsObj) => {
  const maxInstructorsAssignmentByLink =
    settingsObj[settingsType?.SchedulerEditorMaxInstructorsAssignmentByLink];

  const value = !!maxInstructorsAssignmentByLink?.value
    ? JSON.parse(maxInstructorsAssignmentByLink?.value)
    : null;

  return value;
};

const getPackageConfigurationsEnabled = (settingsObj: SettingsObj): boolean => {
  const packageConfigurations = settingsObj[settingsType?.PackageConfigurations];

  const value = !!packageConfigurations?.value
    ? JSON.parse(packageConfigurations?.value)?.publishing?.enabled
    : false;

  return value;
};

const getShowRetakersColumns = (settingsObj: SettingsObj): boolean => {
  const showRetakersColumns = settingsObj[settingsType?.SchedulerEditorShowRetakersColumns];
  const raw = showRetakersColumns?.value;
  return TRUTHY_SETTING_VALUES.includes(raw as any);
};

const getShowContractedBlocksInSchedule = (settingsObj: SettingsObj): boolean => {
  const showContractedBlocks =
    settingsObj[settingsType?.SchedulerEditorShowContractedBlocksInSchedule];
  const raw = showContractedBlocks?.value;
  return TRUTHY_SETTING_VALUES.includes(raw as any);
};

export const getShowKeepSessionDurationCheckbox = (settingsObj: SettingsObj): boolean => {
  const value = settingsObj[settingsType.SchedulerEditorShowKeepSessionDurationCheckbox]?.value;
  return value !== "false" && value !== "0";
};

const getInstructorLinkTags = (settingsObj: SettingsObj): InstructorLinkTagsSetting => {
  const defaultSetting: InstructorLinkTagsSetting = { enabled: false, tags: [] };
  const instructorLinkTags = settingsObj[settingsType?.SchedulerEditorInstructorLinkTags];

  if (!instructorLinkTags?.value) return defaultSetting;

  try {
    const parsed = JSON.parse(instructorLinkTags.value) as InstructorLinkTagsSetting;

    return {
      enabled: parsed?.enabled === true,
      tags: Array.isArray(parsed?.tags) ? parsed.tags : [],
    };
  } catch {
    return defaultSetting;
  }
};

export const getValidateInstructorsAfterRemoval = (
  settingsObj: SettingsObj,
): InstructorRemovalValidationSetting => {
  const setting = settingsObj?.[settingsType.SchedulerEditorValidateInstructorsAfterRemoval];

  if (!setting?.value) return { enabled: false };

  try {
    const parsed = JSON.parse(setting.value) as InstructorRemovalValidationSetting;
    return { enabled: parsed?.enabled === true };
  } catch {
    return { enabled: false };
  }
};

/**
 * The approval flow is on only for a strict boolean `enabled: true` in the
 * `VersionControl.configurations` JSON (the `tenant` value is not used by the front).
 */
export const getIsApprovalFlowEnabled = (settingsObj: SettingsObj): boolean => {
  const setting = settingsObj?.[settingsType.VersionControl];

  if (!setting?.value) return false;

  try {
    return JSON.parse(setting.value)?.enabled === true;
  } catch {
    return false;
  }
};

/**
 * Reads agents chat enablement from its existing backend setting.
 *
 * @param settingsObj - Settings indexed by their backend configuration key.
 * @returns True only for boolean enabled: true; missing or invalid values return false.
 */
export const getIsAgentsChatEnabled = (settingsObj: SettingsObj): boolean => {
  const agentsChat = settingsObj[settingsType.AgentsChat];

  if (!agentsChat?.value) {
    return false;
  }

  try {
    const parsed = JSON.parse(agentsChat.value) as { enabled?: boolean };

    return parsed?.enabled === true;
  } catch {
    return false;
  }
};

const useGetSettings = (): [{ data: boolean; error: boolean; loading: boolean }, () => void] => {
  const client = useApolloClient();
  const { dispatch } = useContext(ContextEDH);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const mouseflowConnect = (settingsObj: SettingsObj) => {
    const findInstitution = settingsObj[settingsType?.institutionCode];
    const findProjectId = settingsObj[settingsType?.mouseflowProjectId];

    if (findProjectId) {
      const projectId = findProjectId?.value.replaceAll('"', "");
      const mf = document.createElement("script");
      mf.type = "text/javascript";
      mf.defer = true;
      mf.src = `//cdn.mouseflow.com/projects/${projectId}.js`;
      document.getElementsByTagName("head")[0].appendChild(mf);
      dispatch({ type: Types.SetMouseflowEnabled, payload: true });
    }

    if (findInstitution && findProjectId) {
      window["_mfq"] = window["_mfq"] || [];
      window["_mfq"].push(["tag", findInstitution?.value.replaceAll('"', "")]);
    }
  };

  const setBookingsProgramRequired = (settingsObj: SettingsObj) => {
    const bookingsProgramRequired = settingsObj[settingsType?.BookingsProgramRequired];
    const value = !!bookingsProgramRequired?.value
      ? JSON.parse(bookingsProgramRequired?.value)
      : null;

    dispatch({
      type: Types.setBookingsProgramRequired,
      payload: value || "HIDDEN",
    });
  };

  const setGroupsInLinkRestriction = (settingsObj: SettingsObj) => {
    const groupsInLinkRestrictionValue = getGroupsInLinkRestriction(settingsObj);

    dispatch({
      type: Types.setGroupsInLinkRestriction,
      payload: groupsInLinkRestrictionValue,
    });
  };

  const setBookingClassroomOrder = (settingsObj: SettingsObj) => {
    const bookingClassroomOrder = settingsObj[settingsType?.SchedulerEditorOrderClassroomBookings];
    const value = !!bookingClassroomOrder?.value
      ? (JSON.parse(bookingClassroomOrder?.value) as {
          enabled: boolean;
          classroom_availabilities_order: BookingClassroomOrderPayload;
        })
      : null;

    dispatch({
      type: Types.setBookingClassroomOrder,
      payload: value?.enabled
        ? (value?.classroom_availabilities_order as BookingClassroomOrderPayload)
        : defaultBookingClassroomOrder,
    });
  };

  const setGroupsManagerType = (settingsObj: SettingsObj) => {
    const groupsManagerConfiguration = getGroupsManagerConfiguration(settingsObj);
    const groupsManagerType =
      Object.keys(groupsManagerConfiguration?.groups_creation_rules || {})[0] ?? "SHARED";

    dispatch({
      type: Types.setGroupsManagerType,
      payload: groupsManagerType as GroupsManagerType,
    });
  };

  const setSchedulerEditorClearLinkResources = (settingsObj: SettingsObj) => {
    const schedulerEditorClearLinkResources =
      settingsObj[settingsType?.SchedulerEditorClearLinkResources];
    const value = !!schedulerEditorClearLinkResources?.value
      ? JSON.parse(schedulerEditorClearLinkResources?.value)
      : null;

    dispatch({
      type: Types.setSchedulerEditorClearLinkResources,
      payload: value,
    });
  };

  const setMaxInstructorsAssignmentByLink = (settingsObj: SettingsObj) => {
    const maxInstructorsAssignmentByLink = getMaxInstructorsAssignmentByLink(settingsObj);
    setRuleCourseTypeSetting(maxInstructorsAssignmentByLink ?? DEFAULT_RULES_COURSE_TYPES);
  };

  const setIsMicroprogrammingStructuresEnabled = (settingsObj: SettingsObj) => {
    const coreProgrammingStructures = settingsObj[settingsType?.CoreProgrammingStructures];
    const value = !!coreProgrammingStructures?.value
      ? JSON.parse(coreProgrammingStructures?.value)
      : null;

    setIsProgrammingStructuresEnabled(value ?? DEFAULT_RULES_COURSE_TYPES);
  };

  const setGeneralSettings = (settingsObj: SettingsObj) => {
    const groupsManagerHideColumnsFilter =
      settingsObj[settingsType?.groupsManagerHideColumnsFilter];
    const shouldHideGroupsColumnsFilter = ["1", "true"].includes(
      groupsManagerHideColumnsFilter?.value || "",
    );

    setGroupsInLinkRestriction(settingsObj);
    setBookingClassroomOrder(settingsObj);
    setBookingsProgramRequired(settingsObj);
    setGroupsManagerType(settingsObj);
    setSchedulerEditorClearLinkResources(settingsObj);
    setMaxInstructorsAssignmentByLink(settingsObj);
    setShowRetakersColumns(getShowRetakersColumns(settingsObj));
    setIsMicroprogrammingStructuresEnabled(settingsObj);
    setInstructorLinkTags(getInstructorLinkTags(settingsObj));
    setShowContractedBlocksInSchedule(getShowContractedBlocksInSchedule(settingsObj));
    setCanPublishPackages(getPackageConfigurationsEnabled(settingsObj));
    setIsAgentsChatEnabled(getIsAgentsChatEnabled(settingsObj));

    dispatch({
      type: Types.setRestrictionConfigurations,
      payload: getRestrictionConfigurations(settingsObj),
    });

    dispatch({
      type: Types.setCanCreateLinkWithoutNewSections,
      payload: getAllowLinkCreationWithoutNewSections(settingsObj),
    });

    dispatch({
      type: Types.setCampusCategoriesConfig,
      payload: getCampusCategoriesConfig(settingsObj),
    });

    dispatch({
      type: Types.setInstitutionThemePreferences,
      payload: getInstitutionThemePreferences(settingsObj),
    });

    const createGroupWithinLinkCreation =
      settingsObj?.[settingsType?.SchedulerEditorCreateGroupWithinLinkCreation];

    if (createGroupWithinLinkCreation?.value) {
      const value = JSON.parse(createGroupWithinLinkCreation?.value) as {
        enabled: boolean;
        creation_type: "MANDATORY | OPTIONAL";
      };
      const payload = value.enabled ? value.creation_type : "DISABLED";

      dispatch({
        type: Types.setCreateGroupWithinLinkCreation,
        payload: payload as "MANDATORY" | "OPTIONAL" | "DISABLED",
      });
    }

    dispatch({
      type: Types.setGroupManagerFilterEnabled,
      payload: !shouldHideGroupsColumnsFilter,
    });
  };

  const getSettings = async () => {
    setLoading(true);
    setValidateInstructorsAfterRemoval({ enabled: false });
    setIsApprovalFlowEnabled(false);
    setAreSettingsLoaded(false);
    setShowKeepSessionDurationCheckbox(true);
    try {
      if (!localStorage.getItem("token")) return;

      const filter: SettingFilterInput = {
        fields: {
          config: {
            in: [
              settingsType.institutionCode,
              settingsType.mouseflowProjectId,
              settingsType.groupsManagerHideColumnsFilter,
              settingsType.groupsManagerConfiguration,
              settingsType.SchedulerEditorCreateGroupWithinLinkCreation,
              settingsType.SchedulerEditorRestrictGroupsInLink,
              settingsType.SchedulerEditorOrderClassroomBookings,
              settingsType.SchedulerEditorCampusCategoriesConfig,
              settingsType.SchedulerEditorClearLinkResources,
              settingsType.BookingsProgramRequired,
              settingsType.SchedulerEditorAllowLinkCreationWithoutNewSections,
              settingsType.RestrictionConfigurations,
              settingsType.InstitutionThemePreferences,
              settingsType.CustomI18nOverrides,
              settingsType.SchedulerEditorMaxInstructorsAssignmentByLink,
              settingsType.SchedulerEditorShowRetakersColumns,
              settingsType.SchedulerEditorInstructorLinkTags,
              settingsType.SchedulerEditorShowContractedBlocksInSchedule,
              settingsType.SchedulerEditorShowKeepSessionDurationCheckbox,
              settingsType.SchedulerEditorValidateInstructorsAfterRemoval,
              settingsType.CoreProgrammingStructures,
              settingsType.AgentsChat,
              settingsType.PackageConfigurations,
              settingsType.VersionControl,
            ],
          },
        },
      };
      const variables = {
        query: settingsQuery,
        variables: {
          filter,
        },
      };
      const { data } = await client.query(variables);
      const baseQuery: BaseQuery = data?.base;
      const settings = baseQuery?.settings?.items;
      dispatch({ type: Types.SetSettings, payload: settings });
      const settingsObj = getSettingsObj(settings ?? []);
      setShowKeepSessionDurationCheckbox(getShowKeepSessionDurationCheckbox(settingsObj));

      // Reset this opt-in setting for a workspace that does not expose it,
      // instead of retaining the previous workspace's enabled value.
      setValidateInstructorsAfterRemoval(getValidateInstructorsAfterRemoval(settingsObj));
      setIsApprovalFlowEnabled(getIsApprovalFlowEnabled(settingsObj));

      if (settings?.length > 0) {
        mouseflowConnect(settingsObj);
        setGeneralSettings(settingsObj);

        const customI18nOverrides = settingsObj[settingsType.CustomI18nOverrides];

        if (customI18nOverrides?.value) {
          try {
            const overrides = JSON.parse(customI18nOverrides.value) as {
              en?: Record<string, any>;
              es?: Record<string, any>;
            };

            applyLocaleOverrides({
              en: overrides?.en ?? null,
              es: overrides?.es ?? null,
            });
          } catch (error) {
            console.error("[Settings] Error parsing custom i18n overrides:", error);
          }
        }
      }

      setResult(true);
      setError(false);
      setLoading(false);
    } catch (error) {
      console.error("[Settings]", error);
      dispatch({ type: Types.SetSettings, payload: [] });
      setResult(true);
      setError(true);
      setLoading(false);
    } finally {
      // Also without a session: the approval-flow roles wait for it and would stay unresolved.
      setAreSettingsLoaded(true);
    }
  };

  return [{ data: result, error, loading }, getSettings];
};

export default useGetSettings;
